import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { MDSModal } from "@/mds";
import { WizardContainer } from "@/components/onboarding/components/welcome-wizard";
import type { StepConfig } from "@/components/onboarding/components/welcome-wizard/onboarding-steps.types";
import { Steps } from "@/components/onboarding/components/welcome-wizard/onboarding-steps.config";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { readIMessageClaimFlowSession } from "@/modules/mem-agent/imessage-claim-flow-session";
import { ONBOARDING_STEPS_WITH_FLOATING_AGENT } from "@/store/floating-agent/floating-agent-onboarding-steps";

interface WelcomeOverlayProps {}

const ONBOARDING_SCRIM_EXIT_DURATION_SECONDS = 0.5;

export const WelcomeOverlay = observer<WelcomeOverlayProps>(() => {
  const { store } = useAppStore();
  const onboarding = store.onboarding;
  const lastStepConfigRef = useRef<StepConfig | undefined>(undefined);
  const isOpen = onboarding.isActive;
  const isSuppressed =
    store.platformInfo.welcomeToProactiveModalIsOpen ||
    store.subscriptions.upgradePlanFlowIsActive ||
    readIMessageClaimFlowSession();
  const substepId = onboarding.currentSubstepId;
  const stepConfig = substepId ? Steps[substepId] : undefined;

  useTrackView({
    currentStepId: onboarding.currentStepId,
    currentSubstepId: stepConfig?.id,
    isVisible: isOpen && !isSuppressed && Boolean(stepConfig),
  });

  if (isSuppressed) {
    return null;
  }

  if (stepConfig) {
    lastStepConfigRef.current = stepConfig;
  }

  if (isOpen && !stepConfig) return null;

  const renderedStepConfig = stepConfig ?? lastStepConfigRef.current;
  if (!renderedStepConfig) return null;
  const shouldAvoidFloatingAgentMessages =
    ONBOARDING_STEPS_WITH_FLOATING_AGENT.has(renderedStepConfig.id) &&
    store.floatingAgent.hasVisibleReplyPreviews;

  const stepLayout =
    typeof renderedStepConfig.layout === "function"
      ? renderedStepConfig.layout()
      : renderedStepConfig.layout;
  const stepContent = renderedStepConfig.render({
    onComplete: (...completedSteps: string[]) => {
      void onboarding.completeCurrentStep(completedSteps);
    },
  });

  if (renderedStepConfig.ownsModal) {
    return <>{stepContent}</>;
  }

  return (
    <OnboardingModal
      isOpen={isOpen}
      handleClose={() => {}}
      isDismissable={false}
    >
      <ModalContent
        data-step-id={renderedStepConfig.id}
        data-floating-agent-messages-visible={
          shouldAvoidFloatingAgentMessages ? "true" : undefined
        }
      >
        <WizardContainer
          data-component="wizard-container"
          data-layout={stepLayout}
        >
          {stepContent}
        </WizardContainer>
      </ModalContent>
    </OnboardingModal>
  );
});

interface TrackViewParams {
  currentStepId: string | undefined;
  currentSubstepId: string | undefined;
  isVisible: boolean;
}

const useTrackView = ({
  currentStepId,
  currentSubstepId,
  isVisible,
}: TrackViewParams) => {
  const { store } = useAppStore();

  useEffect(() => {
    if (!isVisible) return;

    store.metrics.trackWelcomeOverlayView();

    if (currentStepId === "icp-rfi" && currentSubstepId === "referrer") {
      store.metrics.trackNewUserOnboardingViewed();
    }
  }, [currentStepId, currentSubstepId, isVisible, store.metrics]);
};

const onboardingModalExit = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const OnboardingModal = styled(MDSModal)`
  background: ${({ theme }) => theme.colors.surface.overlay.onboarding};
  color: #fff;
  backdrop-filter: blur(2px);

  &[data-exiting] {
    animation: ${onboardingModalExit} ${ONBOARDING_SCRIM_EXIT_DURATION_SECONDS}s
      ease-in-out forwards;
  }
`;

const ModalContent = styled.div`
  overflow: visible;
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translate(-50%, -50%);

  @media (max-width: 600px) {
    &[data-step-id="mem-agent-first-message"][data-floating-agent-messages-visible="true"],
    &[data-step-id="existing-user-mem-agent-first-message"][data-floating-agent-messages-visible="true"] {
      top: 5vw;
      transform: translateX(-50%);
    }
  }
`;
